import { useStyles } from '@archipro-design/aria';
import * as S from './PaintPickerShell.style';
import type { MouseEvent, RefObject } from 'react';
import { useState } from 'react';
import { useEffect, useRef } from 'react';
import { useDebounceFn } from 'ahooks';
import { IconPlus } from '@archipro-design/icons';
import type { Coordinates, RGB } from '../../type';
import { calculateColourAtPoint } from '../../util/paint-picker-util';
import { drawImageOnCanvas } from '../../util/draw-image-on-canvas';
import { usePaintPickerContext } from '~/modules/paint-picker-new/context/PaintPickerContext';
import {
    DEBOUNCE_COLOR_DETECT_DESKTOP,
    DEBOUNCE_VALUE,
    PALETTE_DEFAULT_LIMIT,
} from '@modules/paint-picker-new/config';
import { usedRandomPaintFromImage } from '@modules/paint-picker-new/util/get-random-paint-from-image';
import { useLogger } from '@archipro-website/logger/client';
import { useTracker } from '@archipro-website/tracker';

interface PaintPickerShellProps {
    imageRef: HTMLImageElement | null;
    imageSrc: string;
}

const PaintPickerShell = ({ imageRef, imageSrc }: PaintPickerShellProps) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const [coordinates, setCoordinates] = useState<Coordinates>({ x: 0, y: 0 });
    const tracker = useTracker();

    const {
        state: { isPicking, activeImageSrc, previewPaint, populateDefaults },
        matchRgbToReseneColour,
        onSelectPreview,
        updatePaintSelection,
        addPaintToSelection,
    } = usePaintPickerContext();

    const logger = useLogger();

    const [cursorCoordinates, setCursorCoordinates] = useState<Coordinates>({
        x: 0,
        y: 0,
    });
    const [cursorVisible, setCursorVisible] = useState(false);
    const [currentColor, setCurrentColor] = useState<RGB | null>(null);
    const { css } = useStyles({
        isPicking,
        cursorCoordinates,
        cursorVisible,
        currentColor,
    });

    const { run: debounceCoordinate, cancel: stopDebounce } = useDebounceFn(
        (coordinates: Coordinates) => {
            setCoordinates(coordinates);
        },
        {
            wait: DEBOUNCE_COLOR_DETECT_DESKTOP,
            trailing: true,
        }
    );

    const { run: debounceDrawCanvas } = useDebounceFn(
        (
            canvasRef: RefObject<HTMLCanvasElement>,
            imageRef: HTMLImageElement
        ) => {
            (async () => {
                if (!canvasRef.current || !imageRef) {
                    return;
                }

                await drawImageOnCanvas(canvasRef.current, imageRef).catch(
                    (error) => {
                        logger.error('could not draw image canvas', error);
                    }
                );
            })();
        },
        {
            wait: DEBOUNCE_VALUE,
            maxWait: DEBOUNCE_VALUE,
            trailing: true,
        }
    );

    const getCoordinates = (event: MouseEvent<HTMLDivElement>): Coordinates => {
        const box = event.currentTarget.getBoundingClientRect();
        return {
            x: event.clientX - box.left,
            y: event.clientY - box.top,
        } as Coordinates;
    };

    const onMouseMove = (event: MouseEvent<HTMLDivElement>) => {
        if (isPicking) {
            if (!cursorVisible) {
                setCursorVisible(true);
            }

            const coordinates = getCoordinates(event);
            setCursorCoordinates(coordinates);
            debounceCoordinate(coordinates);
        }
    };

    const onClick = (event: MouseEvent<HTMLDivElement>) => {
        if (isPicking && canvasRef.current) {
            const coordinates = getCoordinates(event);
            const color = calculateColourAtPoint(
                coordinates.x,
                coordinates.y,
                canvasRef.current
            );

            if (color) {
                const paintColour = matchRgbToReseneColour(color);
                if (paintColour) {
                    addPaintToSelection(paintColour);
                    return;
                }
            }
        }

        isPicking && previewPaint && addPaintToSelection(previewPaint);
    };

    useEffect(() => {
        if (imageRef && canvasRef.current) {
            window.addEventListener('resize', () =>
                debounceDrawCanvas(canvasRef, imageRef)
            );
            return () =>
                window.removeEventListener('resize', () =>
                    debounceDrawCanvas(canvasRef, imageRef)
                );
        }
    }, [imageRef, debounceDrawCanvas]);

    useEffect(() => {
        (async () => {
            if (!imageRef || !canvasRef.current) {
                return;
            }

            try {
                await drawImageOnCanvas(canvasRef.current, imageRef);

                if (populateDefaults && activeImageSrc) {
                    const paints = usedRandomPaintFromImage(
                        canvasRef.current,
                        matchRgbToReseneColour,
                        PALETTE_DEFAULT_LIMIT,
                        activeImageSrc
                    );

                    updatePaintSelection(paints);

                    if (paints.length) {
                        onSelectPreview(paints[paints.length - 1] ?? null);
                    }
                }
            } catch (error) {
                logger.error('could not draw image canvas', error);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [imageSrc, activeImageSrc, populateDefaults, imageRef]);

    useEffect(() => {
        if (coordinates.x && coordinates.y && canvasRef?.current) {
            const color = calculateColourAtPoint(
                coordinates.x,
                coordinates.y,
                canvasRef.current
            );

            setCurrentColor(color);

            if (color) {
                const paintColour = matchRgbToReseneColour(color);
                if (paintColour && paintColour.id !== previewPaint?.id) {
                    onSelectPreview(paintColour);
                }
            }
        }
    }, [
        coordinates,
        matchRgbToReseneColour,
        onSelectPreview,
        tracker,
        previewPaint,
    ]);

    return (
        <div
            className={`${css(S.PaintPickerShellStyle)} paint-picker-shell`}
            onMouseMove={(e) => onMouseMove(e)}
            onClick={onClick}
            onMouseLeave={() => {
                stopDebounce();
                setCoordinates({ x: 0, y: 0 });
                setCursorVisible(false);
            }}
        >
            <div
                className={`${css(S.PaintPickerShellContainer)} paint-picker-container`}
            >
                <div className={css(S.PickerCursor)}>
                    <IconPlus className={css(S.PickerCursorIcon)} />
                </div>
                <canvas ref={canvasRef} className={css(S.CanvasStyling)} />
            </div>
        </div>
    );
};

export default PaintPickerShell;
